import _ from 'lodash'
import { EnvironmentDetail, Exception } from '@amzn/ihm-associate-tools-js-lib'
import { cordovaExec } from '@services/CandyCaneService'
import { EnvironmentPluginConstants } from '@utils/Constants/EnvironmentPluginConstants'
import { store } from '@atredux/store'
import { setIhmLoginTokenInfo, setIsLoggedIn } from '@atredux/slices/ihmLoginTokenInfoSlice'
import {
    getAccessToken,
    getIdToken,
    getScopes,
    getTokenType,
} from '@atredux/selectors/ihmLoginTokenInfoSelector'

export class IhmLoginTokenProvider {

    public static readonly IHM_LOGIN_TOKEN_MEMBERS: any = {
        FULL: 'full',
        ACCESS_TOKEN: 'accessToken',
        ID_TOKEN: 'idToken',
        TOKEN_TYPE: 'tokenType',
    }
    private static readonly IHM_LOGIN_TOKEN_DATA_KEY: string = 'ihmLoginTokenData'
    private static readonly IHM_LOGIN_TOKEN_STORAGE_KEY: string = 'ihmLoginTokenScopes'
    // This is to enable IhmLogin based authentication for automation testing in pipelines
    private static readonly ALLOWED_ENVIRONMENTS_FOR_LOCALSTORAGE = ['test', 'gamma']

    public static async clear () {
        store.dispatch(setIhmLoginTokenInfo({
            _ihmLoginToken: null,
            _ihmLoginIdToken: null,
            _tokenType: null,
            _ihmLoginTokenScopes: null,
        }))
        if (IhmLoginTokenProvider.ALLOWED_ENVIRONMENTS_FOR_LOCALSTORAGE.includes(EnvironmentDetail.getDomain()) && !EnvironmentDetail.isAndroid()) {
            window.localStorage.removeItem(IhmLoginTokenProvider.IHM_LOGIN_TOKEN_DATA_KEY)
            window.localStorage.removeItem(IhmLoginTokenProvider.IHM_LOGIN_TOKEN_STORAGE_KEY)
            window.localStorage.removeItem('ALLOWED_ENVIRONMENTS_FOR_LOCALSTORAGE')
        } else {
            await cordovaExec(EnvironmentPluginConstants.ENVIRONMENT_PLUGIN_NAME,
                EnvironmentPluginConstants.ACTION_SET_IHM_LOGIN_TOKEN,
                [IhmLoginTokenProvider.constructJson(IhmLoginTokenProvider.IHM_LOGIN_TOKEN_DATA_KEY, {})])
        }
    }

    public static setToken (accessToken, tokenType, tokenScopes, idToken) {
        if (_.isEmpty(accessToken) || _.isEmpty(tokenType)) {
            throw new Exception({ message : 'IhmLogin access token and token type must be set.' })
        }
        tokenScopes = tokenScopes || []
        tokenScopes = JSON.stringify(tokenScopes)
        // Token Scope is a non-sensitive information
        window.localStorage.setItem(IhmLoginTokenProvider.IHM_LOGIN_TOKEN_STORAGE_KEY, tokenScopes)
        store.dispatch(setIhmLoginTokenInfo({
            _ihmLoginToken: accessToken,
            _ihmLoginIdToken: idToken,
            _tokenType: tokenType,
            _ihmLoginTokenScopes: tokenScopes,
        }))
        store.dispatch((setIsLoggedIn(true)))
        const tokenData = JSON.stringify({
            accessToken, tokenType, idToken,
        })
        if (IhmLoginTokenProvider.ALLOWED_ENVIRONMENTS_FOR_LOCALSTORAGE.includes(EnvironmentDetail.getDomain()) && !EnvironmentDetail.isAndroid()) {
            window.localStorage.setItem(IhmLoginTokenProvider.IHM_LOGIN_TOKEN_DATA_KEY, tokenData)
            window.localStorage.setItem('ALLOWED_ENVIRONMENTS_FOR_LOCALSTORAGE', EnvironmentDetail.getDomain())
            return Promise.resolve()
        } else {
            return cordovaExec(EnvironmentPluginConstants.ENVIRONMENT_PLUGIN_NAME,
                EnvironmentPluginConstants.ACTION_SET_IHM_LOGIN_TOKEN,
                [IhmLoginTokenProvider.constructJson(IhmLoginTokenProvider.IHM_LOGIN_TOKEN_DATA_KEY, tokenData)])
        }
    }

    public static getTokenData () {
        return IhmLoginTokenProvider.retrieveTokenData(IhmLoginTokenProvider.IHM_LOGIN_TOKEN_MEMBERS.FULL)
    }

    public static async getAccessToken () {
        if (getAccessToken(store.getState())) {
            return Promise.resolve(getAccessToken(store.getState()))
        } else {
            return IhmLoginTokenProvider.retrieveTokenData(IhmLoginTokenProvider.IHM_LOGIN_TOKEN_MEMBERS.ACCESS_TOKEN)
        }
    }

    public static async getIdToken () {
        if (getIdToken(store.getState())) {
            return Promise.resolve(getIdToken(store.getState()))
        } else {
            return IhmLoginTokenProvider.retrieveTokenData(IhmLoginTokenProvider.IHM_LOGIN_TOKEN_MEMBERS.ID_TOKEN)
        }
    }

    public static async getTokenType () {
        if (getTokenType(store.getState())) {
            return Promise.resolve(getTokenType(store.getState()))
        } else {
            return IhmLoginTokenProvider.retrieveTokenData(IhmLoginTokenProvider.IHM_LOGIN_TOKEN_MEMBERS.TOKEN_TYPE)
        }
    }

    public static getScopes () {
        const scopesJson = getScopes(store.getState()) || window.localStorage.getItem(IhmLoginTokenProvider.IHM_LOGIN_TOKEN_STORAGE_KEY)
        if (!scopesJson) {
            return []
        }
        try {
            return JSON.parse(scopesJson)
        } catch (err) {
            return []
        }
    }

    public static async isTokenSet () {
        const accessToken = await IhmLoginTokenProvider.retrieveTokenData(IhmLoginTokenProvider.IHM_LOGIN_TOKEN_MEMBERS.ACCESS_TOKEN)
        return !_.isEmpty(accessToken)
    }

    private static async retrieveTokenData (key) {
        try {
            let tokenData
            if (IhmLoginTokenProvider.ALLOWED_ENVIRONMENTS_FOR_LOCALSTORAGE.includes(EnvironmentDetail.getDomain()) && !EnvironmentDetail.isAndroid()) {
                tokenData = window.localStorage.getItem(IhmLoginTokenProvider.IHM_LOGIN_TOKEN_DATA_KEY)
            } else {
                tokenData = await cordovaExec(EnvironmentPluginConstants.ENVIRONMENT_PLUGIN_NAME,
                    EnvironmentPluginConstants.ACTION_GET_IHM_LOGIN_TOKEN,
                    [{ inputKey: IhmLoginTokenProvider.IHM_LOGIN_TOKEN_DATA_KEY }])
            }
            if (key !== IhmLoginTokenProvider.IHM_LOGIN_TOKEN_MEMBERS.FULL) {
                return tokenData !== null ? JSON.parse(tokenData)[key] : {}
            } else {
                return (tokenData !== null ? JSON.parse(tokenData) : {})
            }
        } catch (err) {
            console.log('ATBase: Error in fetching token data')
            return {}
        }
    }

    private static constructJson (jsonKey, jsonValue) {
        const jsonObj = {}
        jsonObj[jsonKey] = jsonValue
        return jsonObj
    }
}
